import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  /** Path-only canonical (e.g. "/product/kashmiri-saffron"). Defaults to current path. */
  canonicalPath?: string;
  image?: string;
  type?: "website" | "product" | "article";
  /** Pre-built JSON-LD object(s). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
  /** Preload hero image for LCP. */
  preloadImage?: string;
  /** Optional keywords meta tag (comma-joined). */
  keywords?: string[];
  /** Article-only metadata. */
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  /** Optional prev/next URLs for paginated or sequential content. */
  prevPath?: string;
  nextPath?: string;
  /** Article-only: section/category and tag list for OG. */
  articleSection?: string;
  articleTags?: string[];
  /** Optional alt text for the Open Graph / Twitter image. */
  imageAlt?: string;
}

const SITE_NAME = "Param Kesar®";

const getOrigin = () => {
  if (typeof window !== "undefined") return window.location.origin;
  return "https://paramkesar.co";
};

export const SEO = ({
  title,
  description,
  canonicalPath,
  image,
  type = "website",
  jsonLd,
  noindex = false,
  preloadImage,
  keywords,
  publishedTime,
  modifiedTime,
  author,
  prevPath,
  nextPath,
  articleSection,
  articleTags,
  imageAlt,
}: SEOProps) => {
  const origin = getOrigin();
  const path =
    canonicalPath ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  const canonical = `${origin}${path}`;
  const ogImage = image
    ? image.startsWith("http")
      ? image
      : `${origin}${image}`
    : `${origin}/og-image.jpg?v=20260508`;
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description.slice(0, 160)} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en-us" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />
      {prevPath && <link rel="prev" href={`${origin}${prevPath}`} />}
      {nextPath && <link rel="next" href={`${origin}${nextPath}`} />}
      <html lang="en" />
      <meta property="og:locale" content="en_US" />
      {/* Preconnect to Supabase is set statically in index.html to avoid duplicate hint */}
      <link rel="alternate" type="application/rss+xml" title="Param Kesar® — The Kesar Kitchen™ (RSS)" href="https://edimpcprihqpzjfhrebb.supabase.co/functions/v1/journal-rss" />
      <link rel="alternate" type="application/feed+json" title="Param Kesar® — The Kesar Kitchen™ (JSON Feed)" href="https://edimpcprihqpzjfhrebb.supabase.co/functions/v1/journal-rss?format=json" />
      {preloadImage && <link rel="preload" as="image" href={preloadImage} {...({ fetchpriority: "high" } as any)} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(", ")} />
      )}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description.slice(0, 200)} />
      <meta property="og:type" content={type} />
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && author && (
        <meta property="article:author" content={author} />
      )}
      {type === "article" && articleSection && (
        <meta property="article:section" content={articleSection} />
      )}
      {type === "article" &&
        articleTags &&
        articleTags.map((t) => (
          <meta key={t} property="article:tag" content={t} />
        ))}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:type" content={/\.webp(\?|$)/i.test(ogImage) ? "image/webp" : /\.png(\?|$)/i.test(ogImage) ? "image/png" : "image/jpeg"} />
      <meta property="og:image:width" content={image ? "1600" : "1200"} />
      <meta property="og:image:height" content={image ? "900" : "630"} />
      <meta property="og:image:alt" content={imageAlt || title} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@paramkesar" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description.slice(0, 200)} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={imageAlt || title} />

      {ldArray.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
};
